import { computed, inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AccountService } from '../account/account.service';
import { Membership, ProjectRole } from '../models/membership.model';
import { canManageProject } from '../utils/project-permission.utils';

// Route-scoped: provide on the ':projectId' segment and call ensureLoaded(projectId) from that route's resolver so the id comes from the ActivatedRouteSnapshot (reliable) instead of an ambient ActivatedRoute (empty at resolve time).
@Injectable()
export class ProjectRoleService {
  private readonly accountService = inject(AccountService);

  private readonly projectId = signal<string | null>(null);
  private readonly profileRole = signal<string | null>(null);
  private readonly memberships = signal<Membership[]>([]);
  private loaded$: Observable<boolean> | null = null;
  private loadedFor: string | null = null;

  readonly isPlatformAdmin = computed(() => this.profileRole() === 'PLATFORM_ADMIN');

  readonly isTenantAdmin = computed(() => this.profileRole() === 'TENANT_ADMIN');

  readonly role = computed<ProjectRole | null>(
    () => this.memberships().find((m) => m.projectId === this.projectId())?.role ?? null,
  );

  readonly isProjectReader = computed(() => !this.isPlatformAdmin() && this.role() === 'PROJECT_READER');

  readonly canManageProject = computed(() => canManageProject(this.profileRole(), this.role()));

  // Angular keeps one route injector per route config, not per projectId, so this instance outlives a project switch and must reload to see role changes.
  ensureLoaded(projectId: string | null): Observable<boolean> {
    this.projectId.set(projectId);
    if (!this.loaded$ || this.loadedFor !== projectId) {
      this.loadedFor = projectId;
      this.loaded$ = this.load();
    }
    return this.loaded$;
  }

  private load(): Observable<boolean> {
    const load$: Observable<boolean> = forkJoin({
      profile: this.accountService.getProfile(),
      memberships: this.accountService.getMemberships(),
    }).pipe(
      tap(({ profile, memberships }) => {
        this.profileRole.set(profile.role);
        this.memberships.set(memberships);
      }),
      map(() => true),
      catchError((error) => {
        if (this.loaded$ === load$) {
          this.loaded$ = null;
        }
        // Route activation must not hang on a transient failure; backend @PreAuthorize is the security anchor.
        console.error('ProjectRoleService: failed to load role information, defaulting to reader-free', error);
        return of(true);
      }),
      shareReplay(1),
    );
    return load$;
  }
}
